import React from 'react';
import {
  AutocompleteModule,
  ActionModule,
  SearchModule,
  TagModule,
  ClipboardModule,
  QueryScore,
  isNoCommaString,
} from './Modules';
import { SearchContext, SearchContextType } from '../context';
import { SessionContext } from '@benzinga/session-context';
import { dropdownDescriptionHelper } from '../SearchBar';
import { FilerInfo, GovTradesManager } from '@benzinga/gov-trades';
import {
  GovernmentTradeItemSpecifier,
  GovernmentTradeSearchItemVersion,
  GovernmentTradeSearchModuleId,
  SearchItem,
  createGovernmentTradeSearchItem,
  isLegacyGovernmentTradeSearchItem,
  GovernmentTradeSearchItem,
  GovernmentTradeSearchItemData,
  isGovernmentTradeSearchItem,
} from '@benzinga/search-modules';

export interface GovernmentTradeSearchModule extends SearchModule<GovernmentTradeSearchItemData> {
  id: typeof GovernmentTradeSearchModuleId;
  autocomplete: AutocompleteModule<GovernmentTradeSearchItemData>;
  tag: TagModule;
  action: ActionModule;
  clipboard: ClipboardModule;
}

export const createGovernmentTradeAutocompleteModule = (
  govTradesManager: GovTradesManager,
  searchContext: SearchContextType,
): AutocompleteModule<GovernmentTradeSearchItemData> => {
  return {
    dropdown: (item: SearchItem) => {
      if (!isGovernmentTradeSearchItem(item)) return undefined;
      return {
        component: dropdownDescriptionHelper(GovernmentTradeSearchModuleId, item.data.governmentMemberName),
        id: item.id,
        locked: false,
        searchItem: item,
        type: 'AutocompleteDropdown',
        version: GovernmentTradeSearchItemVersion,
      };
    },
    onSelect: (item: SearchItem) => {
      if (!isGovernmentTradeSearchItem(item)) return undefined;
      return item;
    },
    query: (text: string, limit: number, selectedItems?: SearchItem[]) => {
      let canceled = false;
      const lowercaseText = text.toLowerCase();
      const members = govTradesManager.getMembers().congressional_members?.map(member => member.filer_info) ?? [];
      return {
        cancel: () => {
          canceled = true;
        },
        promise: new Promise(resolve => {
          (async () => {
            if (canceled) return resolve({ canceled: true });
            const filterCongressMembers = members?.filter(
              member =>
                selectedItems == null ||
                !selectedItems.some(
                  item => isGovernmentTradeSearchItem(item) && item.data.governmentMemberId === member.member_id,
                ),
            );
            const scoredItems = filterCongressMembers?.flatMap<[FilerInfo, QueryScore]>(member => {
              if (text === '') {
                if (
                  searchContext
                    .getRecentModuleItem()
                    .some(
                      item => isGovernmentTradeSearchItem(item) && item.data.governmentMemberId === member.member_id,
                    )
                )
                  return [[member, QueryScore.HistoryMatch]];
                return [[member, QueryScore.NoMatch]];
              }
              if (member.display_name.toLowerCase() === lowercaseText) {
                return [[member, QueryScore.FullMatch]];
              }
              if (member.display_name.toLowerCase().startsWith(lowercaseText)) {
                return [[member, QueryScore.StartsWithMatch]];
              }
              if (member.display_name.toLowerCase().includes(lowercaseText)) {
                return [[member, QueryScore.IncludesMatch]];
              }
              return [];
            });
            const sortedItems = scoredItems?.sort((a, b) => b[1] - a[1]) ?? [];
            const result = sortedItems
              ?.slice(undefined, limit)
              .map<
                [SearchItem<GovernmentTradeSearchItemData>, QueryScore]
              >(scoredItems => [createGovernmentTradeSearchItem(scoredItems[0].member_id ?? '', scoredItems[0].display_name ?? ''), scoredItems[1]]);
            resolve({
              canceled: false,
              ok: result,
            });
          })();
        }),
      };
    },
    specifier: GovernmentTradeItemSpecifier,
  };
};

export const createGovernmentTradeTagModule = (): TagModule => {
  return {
    render: item => {
      if (!isGovernmentTradeSearchItem(item)) return undefined;
      return {
        text: item.data.governmentMemberName,
        type: 'text',
      };
    },
    tagInputText: item => {
      if (!isGovernmentTradeSearchItem(item)) return undefined;
      return item.data.governmentMemberName;
    },
  };
};

export const createGovernmentTradeActionModule = (): ActionModule => {
  return {
    dropdown: () => {
      return [];
    },
  };
};

export const createGovernmentTradeClipboardModule = (): ClipboardModule => {
  return {
    onCopyJSON: token => {
      if (!isGovernmentTradeSearchItem(token)) return undefined;
      return [token];
    },
    onCopyString: token => {
      if (!isGovernmentTradeSearchItem(token)) return undefined;
      const str = `${GovernmentTradeItemSpecifier}${token.data.governmentMemberId}:${token.data.governmentMemberName}`;
      return isNoCommaString(str) ? [str] : undefined;
    },
    onPasteJSON: (token): GovernmentTradeSearchItem | undefined => {
      if (isLegacyGovernmentTradeSearchItem(token)) return GovernmentTradeModuleItemLoader(token);
      return undefined;
    },
    onPasteString: str => {
      if (str.startsWith(GovernmentTradeItemSpecifier)) {
        const authorData = str.slice(GovernmentTradeItemSpecifier.length).split(':');
        if (authorData.length !== 2) return undefined;
        return createGovernmentTradeSearchItem(authorData[0], authorData[1]);
      }
      return undefined;
    },
  };
};

const GovernmentTradeModuleItemLoader = (item: SearchItem) => {
  if (!isLegacyGovernmentTradeSearchItem(item)) return undefined;

  switch (item.version) {
    case GovernmentTradeSearchItemVersion:
    default:
      return item;
  }
};

export const createGovernmentTradeSearchModule = (
  govTradesManager: GovTradesManager,
  searchContext: SearchContextType,
): GovernmentTradeSearchModule => {
  return {
    action: createGovernmentTradeActionModule(),
    autocomplete: createGovernmentTradeAutocompleteModule(govTradesManager, searchContext),
    clipboard: createGovernmentTradeClipboardModule(),
    id: GovernmentTradeSearchModuleId,
    loader: GovernmentTradeModuleItemLoader,
    tag: createGovernmentTradeTagModule(),
  };
};

export const useGovernmentTradeSearchModule = () => {
  const session = React.useContext(SessionContext);
  const searchContext = React.useContext(SearchContext);
  const govTradesManager = session.getManager(GovTradesManager);
  return React.useMemo(
    () => createGovernmentTradeSearchModule(govTradesManager, searchContext),
    [govTradesManager, searchContext],
  );
};
